<script setup lang="ts">
/**
 * 〈市集 › 審核〉（`/plugin-reviews`；批153 批B）——admin 以上可看、可下載包；**核准與婉拒要營運主管**。
 *
 * 兩個分頁，資料都來自 `GET /reviews`（一次載回，數量本來就少）：
 * - **上架申請**：第三方發行者在 portal 送出的公開上架申請（`listing_status='pending'`）。
 *   核准＝批A 的 #14 publish（同時把 `listing_status` 寫成 `approved`）；公開＝對全世界發布，
 *   所以和詳情頁一樣要逐字輸入 plugin key。婉拒要填理由（發行者在 portal 看得到）。
 * - **待審版本**：已公開的第三方 plugin 上傳的新版（`review_status='pending'`）。核准後 LS 把包從
 *   審核 channel 搬到主 channel 才正式發布；婉拒後那個版號可以重傳。
 *
 * 〈下載包〉走 fetch＋`Authorization` 標頭再轉 blob 存檔——**token 不進 URL**。
 * 審核者應該先下載、在隔離環境裝起來看過再核准。
 */
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'

import UiButton from '@/components/UiButton.vue'
import UiModal from '@/components/UiModal.vue'
import * as pm from '@/api/pluginMarket'
import type { MarketPlugin, ReviewQueue, ReviewVersion } from '@/api/pluginMarket'
import { saveBlobFile } from '@/lib/download'
import { vEnter } from '@/lib/enterKey'
import {
  apiErrorText,
  APPROVAL_PENDING_TEXT,
  canManageMarket,
  formatBytes,
  formatFingerprint,
  latestReleasedVersion,
  packageFilename,
  publishConfirmOk,
  REVIEW_ERROR_TEXT,
  reviewQueueCount,
} from '@/lib/pluginMarket'
import { setPendingReviews } from '@/lib/reviewBadge'
import { fmtSeen } from '@/lib/staleness'
import { useAuthStore } from '@/stores/auth'
import { useDataStore } from '@/stores/data'
import { useDialogsStore } from '@/stores/dialogs'
import { vCardTable } from '@/lib/cardTable'

const auth = useAuthStore()
const store = useDataStore()
const dialogs = useDialogsStore()
const router = useRouter()

const isManager = computed(() => canManageMarket(auth.role))

type Tab = 'listing' | 'version'
const tab = ref<Tab>('listing')
const queue = ref<ReviewQueue | null>(null)
const loading = ref(false)
const loadError = ref('')

const listings = computed<MarketPlugin[]>(() => queue.value?.listing_pending ?? [])
const versions = computed<ReviewVersion[]>(() => queue.value?.version_pending ?? [])

async function fail(e: unknown, fallback: string) {
  await dialogs.notify({ title: '操作失敗', message: apiErrorText(e, REVIEW_ERROR_TEXT, fallback) })
}

async function load() {
  loading.value = true
  try {
    queue.value = await pm.listReviews()
    loadError.value = ''
    setPendingReviews(reviewQueueCount(queue.value))
  } catch (e) {
    loadError.value = apiErrorText(e, {}, '讀取審核佇列失敗')
  } finally {
    loading.value = false
  }
}
onMounted(async () => {
  void store.ensureProducts().catch(() => {})
  await load()
  // 上架申請是空的、但有待審版本時，直接停在有東西的那一頁。
  if (!listings.value.length && versions.value.length) tab.value = 'version'
})

function openPlugin(id: string) {
  void router.push({ name: 'plugin-detail', params: { id } })
}

// ---------------------------------------------------------------------------
// 下載包
// ---------------------------------------------------------------------------
const downloading = ref('')
async function download(pluginId: string, key: string, versionId: string, version: string) {
  downloading.value = versionId
  try {
    const { blob, filename } = await pm.downloadVersion(pluginId, versionId)
    saveBlobFile(blob, packageFilename(key, version, filename))
  } catch (e) {
    await fail(e, '下載失敗')
  } finally {
    downloading.value = ''
  }
}
/**
 * 上架申請那一列的〈下載包〉：申請的是整支 plugin，下載**最新一個已上架的版本**
 * （審核清單只帶 plugin 投影，版本 id 要另外讀一次詳情）。
 */
async function downloadLatest(p: MarketPlugin) {
  downloading.value = p.id
  try {
    const detail = await pm.getPlugin(p.id)
    // R3-9：只挑**已發布**的版本（審核中的 `status` 也是 ready，但那不是這次上架申請要審的東西）。
    const v = latestReleasedVersion(detail.versions)
    if (!v) {
      await dialogs.notify({ title: '沒有可下載的版本', message: '這個擴充目前沒有已上架的版本。' })
      return
    }
    const { blob, filename } = await pm.downloadVersion(p.id, v.id)
    saveBlobFile(blob, packageFilename(p.key, v.version, filename))
  } catch (e) {
    await fail(e, '下載失敗')
  } finally {
    downloading.value = ''
  }
}

// ---------------------------------------------------------------------------
// 上架申請：核准（＝publish，逐字確認）／婉拒
// ---------------------------------------------------------------------------
const approveTarget = ref<MarketPlugin | null>(null)
const approveTyped = ref('')
const approveBusy = ref(false)
function openApproveListing(p: MarketPlugin) {
  approveTarget.value = p
  approveTyped.value = ''
}
async function doApproveListing() {
  const p = approveTarget.value
  if (!p || !publishConfirmOk(approveTyped.value, p.key)) return
  approveBusy.value = true
  try {
    const res = await pm.publishPlugin(p.id)
    approveTarget.value = null
    await load()
    if (!res.dl_synced) {
      await dialogs.notify({
        title: '已核准上架，但發布主機還沒同步',
        message: '已改成公開；發布主機那一半這次沒有完成，背景會在一分鐘內自動重試。',
      })
    }
  } catch (e) {
    await fail(e, '核准失敗')
  } finally {
    approveBusy.value = false
  }
}
async function rejectListing(p: MarketPlugin) {
  const note = await dialogs.prompt({
    title: `婉拒「${p.name}」的上架申請`,
    message: '婉拒後這個擴充維持私人；理由會顯示給發行者，他修正後可以再申請一次。',
    label: '婉拒理由（必填，發行者看得到）',
    placeholder: '例：說明文件缺少隱私權聲明',
    required: true,
    multiline: true,
    maxLength: 2000,
    danger: true,
    confirmText: '婉拒',
  })
  if (note === null) return
  try {
    await pm.rejectListing(p.id, note)
    await load()
  } catch (e) {
    await fail(e, '婉拒失敗')
  }
}

// ---------------------------------------------------------------------------
// 待審版本：核准／婉拒
// ---------------------------------------------------------------------------
const versionBusy = ref('')
async function approveVersion(v: ReviewVersion) {
  // R3-8：「核准處理中」那一列按〈重試核准〉＝後端原地重跑發布主機那一半，不必再確認一次。
  if (!v.approving) {
    const ok = await dialogs.confirm({
      title: `核准 ${v.plugin.name} ${v.version} 版`,
      message:
        '核准後這一版立刻發布到市集：任何站台都能看到並匿名下載，已安裝的站台會收到「有新版」提示。' +
        '請確認已經下載並檢查過這個包。',
      confirmText: '核准發布',
    })
    if (!ok) return
  }
  versionBusy.value = v.id
  try {
    const res = await pm.approveVersion(v.plugin_id, v.id)
    await load()
    if (!res.dl_synced) {
      await dialogs.notify({ title: '已核准，發布主機還在處理', message: APPROVAL_PENDING_TEXT })
    }
  } catch (e) {
    await fail(e, '核准失敗')
  } finally {
    versionBusy.value = ''
  }
}
async function rejectVersion(v: ReviewVersion) {
  const note = await dialogs.prompt({
    title: `婉拒 ${v.plugin.name} ${v.version} 版`,
    message: '婉拒後這一版不會發布；發行者修正後可以用同一個版號重新上傳。',
    label: '婉拒理由（必填，發行者看得到）',
    placeholder: '例：要求的權限超出說明的用途',
    required: true,
    multiline: true,
    maxLength: 2000,
    danger: true,
    confirmText: '婉拒',
  })
  if (note === null) return
  versionBusy.value = v.id
  try {
    await pm.rejectVersion(v.plugin_id, v.id, note)
    await load()
  } catch (e) {
    await fail(e, '婉拒失敗')
  } finally {
    versionBusy.value = ''
  }
}
</script>

<template>
  <div class="page">
    <div class="page-head">
      <h1>審核</h1>
      <span class="spacer" />
      <UiButton variant="secondary" :loading="loading" @click="load">重新整理</UiButton>
    </div>
    <p class="subtle intro">
      第三方發行者的<strong>公開上架申請</strong>與已公開擴充的<strong>每一個新版本</strong>都要在這裡審過才會出現在市集。
      私人擴充與第一方不必審。
      <template v-if="!isManager">核准與婉拒需要營運主管以上的權限；你可以先下載包檢查。</template>
    </p>

    <div class="tabs" data-hscroll>
      <button type="button" class="tab" :class="{ active: tab === 'listing' }" @click="tab = 'listing'">
        上架申請<span class="tab-count">{{ listings.length }}</span>
      </button>
      <button type="button" class="tab" :class="{ active: tab === 'version' }" @click="tab = 'version'">
        待審版本<span class="tab-count">{{ versions.length }}</span>
      </button>
    </div>

    <div v-if="loading && !queue" class="loading-wrap"><span class="spinner lg" /></div>
    <p v-else-if="loadError" class="err">{{ loadError }}</p>

    <!-- 上架申請 -->
    <div v-else-if="tab === 'listing'" class="card">
      <div class="tbl-scroll" data-hscroll>
        <table v-card-table class="tbl card-table sticky-acts">
          <thead>
            <tr><th>擴充</th><th>發行者</th><th>產品</th><th>最新版</th><th>申請</th><th></th></tr>
          </thead>
          <tbody>
            <tr v-for="p in listings" :key="p.id">
              <td>
                <button type="button" class="link pname" @click="openPlugin(p.id)">{{ p.name }}</button>
                <code class="mono sub">{{ p.key }}</code>
              </td>
              <td class="wide">{{ p.publisher_name ?? '—' }}</td>
              <td class="nowrap">{{ store.productName(p.product) }}</td>
              <td class="nowrap"><code class="mono">{{ p.latest_version ?? '—' }}</code></td>
              <td class="nowrap subtle">
                {{ fmtSeen(p.listing_requested_at, { short: true }) }}
                <div v-if="p.listing_requested_by" class="small">{{ p.listing_requested_by }}</div>
              </td>
              <td class="acts-cell">
                <div class="acts">
                  <UiButton size="sm" variant="secondary" :loading="downloading === p.id" @click="downloadLatest(p)">
                    下載包
                  </UiButton>
                  <template v-if="isManager">
                    <UiButton size="sm" variant="primary" @click="openApproveListing(p)">核准上架…</UiButton>
                    <UiButton size="sm" variant="danger" @click="rejectListing(p)">婉拒…</UiButton>
                  </template>
                </div>
              </td>
            </tr>
            <tr v-if="!listings.length">
              <td colspan="6" class="subtle empty-row">沒有待審的上架申請。</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- 待審版本 -->
    <div v-else class="card">
      <div class="tbl-scroll" data-hscroll>
        <table v-card-table class="tbl card-table sticky-acts">
          <thead>
            <tr><th>擴充</th><th>版號</th><th>發行者</th><th>大小</th><th>簽章指紋</th><th>上傳</th><th>說明</th><th></th></tr>
          </thead>
          <tbody>
            <tr v-for="v in versions" :key="v.id">
              <td>
                <button type="button" class="link pname" @click="openPlugin(v.plugin_id)">{{ v.plugin.name }}</button>
                <code class="mono sub">{{ v.plugin.key }}</code>
              </td>
              <td class="nowrap">
                <code class="mono">{{ v.version }}</code>
                <div v-if="v.approving" class="approving">
                  <span class="status-pill pending">核准處理中</span>
                </div>
              </td>
              <td class="wide">{{ v.plugin.publisher_name ?? '—' }}</td>
              <td class="nowrap subtle">{{ formatBytes(v.size) }}</td>
              <td class="nowrap">
                <code class="mono small" :title="v.signer_fingerprint">{{ formatFingerprint(v.signer_fingerprint.slice(0, 16)) }}</code>
              </td>
              <td class="nowrap subtle">
                {{ fmtSeen(v.ready_at ?? v.created_at, { short: true }) }}
                <div v-if="v.uploaded_by" class="small">{{ v.uploaded_by }}</div>
              </td>
              <td class="notes">{{ v.release_notes || '—' }}</td>
              <td class="acts-cell">
                <div class="acts">
                  <UiButton
                    size="sm"
                    variant="secondary"
                    :loading="downloading === v.id"
                    @click="download(v.plugin_id, v.plugin.key, v.id, v.version)"
                  >
                    下載包
                  </UiButton>
                  <template v-if="isManager">
                    <!-- R3-8：核准處理中（已按過核准、等發布主機那一半）→〈重試核准〉可按（後端原地重跑），婉拒反灰。 -->
                    <UiButton
                      size="sm"
                      variant="primary"
                      :loading="versionBusy === v.id"
                      :title="v.approving ? '發布主機那一半還沒完成；背景約 10 分鐘內會自動接手，也可以手動重試' : undefined"
                      @click="approveVersion(v)"
                    >
                      {{ v.approving ? '重試核准' : '核准…' }}
                    </UiButton>
                    <UiButton
                      size="sm"
                      variant="danger"
                      :disabled="versionBusy === v.id || v.approving"
                      @click="rejectVersion(v)"
                    >
                      婉拒…
                    </UiButton>
                  </template>
                </div>
              </td>
            </tr>
            <tr v-if="!versions.length">
              <td colspan="8" class="subtle empty-row">沒有待審的版本。</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- 核准上架（逐字確認） -->
    <UiModal :open="!!approveTarget" title="核准上架申請" width="540px" @close="approveTarget = null">
      <template v-if="approveTarget">
        <ul class="warn-list">
          <li>核准＝<strong>對全世界發布</strong>：任何站台都能在市集看到「{{ approveTarget.name }}」，並匿名下載每一個已上架的版本。</li>
          <li>發行者是第三方：之後上傳的每一版都會先進審核佇列，核准後才出現在市集。</li>
          <li>要收回請到詳情頁按〈下架改回私人〉（已經下載的人手上的檔案收不回來）。</li>
        </ul>
        <label class="field">
          <span class="field-label">請輸入 <code class="mono">{{ approveTarget.key }}</code> 確認</span>
          <input v-model="approveTyped" class="mono" placeholder="擴充代號" v-enter="doApproveListing" />
        </label>
      </template>
      <template #foot>
        <UiButton @click="approveTarget = null">取消</UiButton>
        <UiButton
          variant="primary"
          :loading="approveBusy"
          :disabled="!approveTarget || !publishConfirmOk(approveTyped, approveTarget.key)"
          @click="doApproveListing"
        >
          核准並公開
        </UiButton>
      </template>
    </UiModal>
  </div>
</template>

<style scoped>
.page-head h1 {
  margin: 0;
  font-size: var(--fs-20);
  color: var(--fg);
}
.spacer {
  flex: 1;
}
.intro {
  margin: 0 0 var(--sp-3);
}
.tabs {
  margin-bottom: var(--sp-3);
}
.tab-count {
  margin-left: 6px;
  font-size: var(--fs-11);
  color: var(--fg-4);
  font-variant-numeric: tabular-nums;
}
.err {
  margin: var(--sp-2) 0;
  color: var(--danger);
  font-size: var(--fs-12);
}
.link {
  border: none;
  background: none;
  padding: 0;
  font: inherit;
  cursor: pointer;
  text-align: left;
}
.pname {
  display: block;
  /* min-width：窄螢幕時別把名稱擠成好幾行（表格在 .tbl-scroll 裡可橫向捲動）。 */
  min-width: 140px;
  font-weight: 600;
  color: var(--fg);
}
.pname:hover {
  color: var(--brand-600);
}
.mono {
  font-family: var(--font-mono);
}
.sub {
  font-size: var(--fs-11);
  color: var(--fg-4);
}
.small {
  font-size: var(--fs-11);
}
.nowrap {
  white-space: nowrap;
}
.wide {
  min-width: 120px;
}
.notes {
  min-width: 160px;
  max-width: 280px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  font-size: var(--fs-12);
  color: var(--fg-2);
}
.empty-row {
  text-align: center;
  padding: var(--sp-5) 0;
}
.approving {
  margin-top: 4px;
}
.warn-list {
  margin: 0 0 var(--sp-3);
  padding-left: 1.2em;
  color: var(--fg-2);
  font-size: var(--fs-13);
  line-height: var(--lh-base);
}
</style>
